import { ReactElement, useState } from "react";

import {
  FieldComponentOverrideProps,
  FieldGroup,
  FieldItem,
  fieldName,
  FieldProps,
  FieldWrapper,
  Label,
} from "@smart/bridge-intake-components-dom";
import {
  convertRoleValueFromSB,
  getContactInfo,
} from "@smart/bridge-smokeball-basic";
import { useDebouncedValue } from "@smart/itops-hooks-dom";
import { loadLocale } from "@smart/itops-locale-dom";
import { SearchFieldInput } from "@smart/itops-sb-design-system-dom";
import { getNestedValue, isNullOrUndefined } from "@smart/itops-utils-basic";
import { Gql } from "@smart/manage-gql-operations-dom";

import { useSearchContacts } from "./search-contacts";

const searchableFieldNames = [
  "person/firstName",
  "person/middleName",
  "person/lastName",
  "company/name",
];

const getResponseValue = (
  field: FieldItem,
  contact: Gql.ContactFieldsFragment,
) => {
  const [contactType, key] = getContactInfo(field.field?.name || "");

  const value =
    contactType &&
    key &&
    contact[contactType] &&
    getNestedValue(contact[contactType], key);
  return value
    ? convertRoleValueFromSB(field.type, value, loadLocale().country)
    : undefined;
};

const saveContactInfo = async ({
  index,
  contact,
  field,
  groups,
  fields,
  visibility,
  submitResponses,
  responses,
}: {
  index: number | undefined;
  contact: Gql.ContactFieldsFragment;
  field: FieldItem;
} & FieldComponentOverrideProps) => {
  const currentFieldGroup = field.groupUri
    ? groups.find((g) => g.uri === field.groupUri)
    : undefined;
  const contactType: "roleContact" | "layoutContact" =
    currentFieldGroup?.type === "layoutContact"
      ? "layoutContact"
      : "roleContact";

  const getResponseValueToUpdate = ({
    fieldToUpdate,
    value,
    indexToUpdate,
  }: {
    fieldToUpdate: FieldItem;
    value: any;
    indexToUpdate: number | undefined;
  }) => {
    if (!fieldToUpdate.groupUri) return value;

    const fieldGroup = groups.find((g) => g.uri === fieldToUpdate.groupUri);
    if (fieldGroup?.repeatable && indexToUpdate !== undefined) {
      const existingResponses = responses[fieldToUpdate.uri] || [];
      return Array.from(
        { length: Math.max(existingResponses.length, indexToUpdate + 1) },
        (_, i) => {
          if (i === indexToUpdate) return value;
          const existingValue = existingResponses[i];
          return existingValue === undefined ? null : existingValue;
        },
      );
    }

    return [value];
  };

  const currentFieldResponseValue = getResponseValue(field, contact);
  const responsesToUpdate = {
    [field.uri]: getResponseValueToUpdate({
      fieldToUpdate: field,
      value: currentFieldResponseValue,
      indexToUpdate: index,
    }),
  };

  const getFieldsToUpdate = () =>
    fields.filter((f) => {
      const isLayoutContactGroupMismatch =
        contactType === "roleContact"
          ? false
          : !f.groupUri || f.groupUri !== field.groupUri;
      const fieldGroup = groups.find((g) => f.groupUri === g.uri);
      const isNotSameRepeatGroup =
        field.groupUri !== f.groupUri &&
        (currentFieldGroup?.repeatable || fieldGroup?.repeatable);
      if (
        !f.layout ||
        !f.field?.name ||
        f.uri === field.uri ||
        isLayoutContactGroupMismatch ||
        isNotSameRepeatGroup
      )
        return false;

      const isVisible =
        visibility.visibleSections[f.sectionUri] &&
        visibility.visibleFields[f.uri] &&
        (!f.groupUri || visibility.visibleGroups[f.groupUri]);
      if (!isVisible) return false;

      return (
        f.field.details === field.field?.details &&
        f.layout.providerId === field.layout?.providerId &&
        f.layout.id === field.layout?.id &&
        f.layout.name === field.layout?.name &&
        f.layout.parentId === field.layout?.parentId &&
        f.layout.parentName === field.layout?.parentName &&
        f.layout.parentProviderId === field.layout?.parentProviderId
      );
    });

  getFieldsToUpdate().forEach((f) => {
    responsesToUpdate[f.uri] = getResponseValueToUpdate({
      fieldToUpdate: f,
      value: getResponseValue(f, contact),
      indexToUpdate: index,
    });
  });

  await submitResponses({
    responses: responsesToUpdate,
  });
};

export const ContactSearchField = ({
  contactType,
  id,
  errorId,
  onSelect,
  dataTestId = "contact-search-input",
  field,
  value,
  error,
  loading,
  disabled,
  onChange,
  onBlur,
}: Pick<
  FieldProps,
  "field" | "value" | "error" | "loading" | "disabled" | "onChange" | "onBlur"
> & {
  contactType: "person" | "company";
  id: string;
  errorId: string;
  onSelect: (item: Gql.ContactFieldsFragment) => void;
  dataTestId?: string;
}) => {
  // Prevent search on initial render or selecting an item
  const [lastAction, setLastAction] = useState<
    "initial" | "select" | "type" | "blur"
  >("initial");
  const searchValue = useDebouncedValue(
    lastAction !== "initial" ? (value ?? "") : "",
    500,
  );
  const { result, loading: searching } = useSearchContacts({
    name: searchValue,
    shouldSearch: lastAction === "type",
  });

  const displayName = ({ person, company }: Gql.ContactFieldsFragment) =>
    [person?.firstName, person?.middleName, person?.lastName, company?.name]
      .filter(Boolean)
      .join(" ");

  const highlightSearchValue = (text: string) => {
    const trimmedSearchValue = searchValue.trim();
    if (!searchValue || trimmedSearchValue === "") {
      return text;
    }

    const parts: ReactElement[] = [];
    let remainingText = text;

    while (remainingText.length > 0) {
      const matchIndex = remainingText
        .toLowerCase()
        .indexOf(trimmedSearchValue.toLowerCase());

      if (matchIndex === -1) {
        parts.push(<span key={Math.random()}>{remainingText}</span>);
        break;
      }

      if (matchIndex > 0) {
        const textBeforeMatch = remainingText.slice(0, matchIndex);
        parts.push(<span key={Math.random()}>{textBeforeMatch}</span>);
      }

      const matchedText = remainingText.slice(
        matchIndex,
        matchIndex + trimmedSearchValue.length,
      );
      parts.push(<strong key={Math.random()}>{matchedText}</strong>);

      remainingText = remainingText.slice(
        matchIndex + trimmedSearchValue.length,
      );
    }

    return <>{parts.map((p) => p)}</>;
  };

  const getItems = () =>
    result
      ?.filter((item) =>
        contactType === "person"
          ? !isNullOrUndefined(item.person?.firstName)
          : !isNullOrUndefined(item.company?.name),
      )
      .map((item) => ({
        label: displayName(item),
        labelOverride: highlightSearchValue(displayName(item)),
        onClick: () => {
          onSelect(item);
          setLastAction("select");
        },
      })) || [];

  return (
    <FieldWrapper aria-disabled={disabled} isLoading={loading}>
      <FieldGroup
        id={id}
        hint={field.hint ?? undefined}
        label={<Label {...field} />}
      >
        <SearchFieldInput
          dataTestId={dataTestId}
          id={id}
          name={id}
          aria-invalid={!!error}
          aria-errormessage={errorId ?? undefined}
          error={!!error}
          message={error ?? undefined}
          aria-autocomplete="list"
          value={value ?? ""}
          onChange={(e) => {
            if (lastAction !== "type") {
              setLastAction("type");
            }
            onChange(e.target.value);
          }}
          onBlur={() => {
            setLastAction("blur");
            onBlur();
          }}
          autoComplete="off"
          fetching={searching}
          onClear={() => {
            // To prevent a bug where the user clears the field and enters the same value again
            if (lastAction !== "type") {
              setLastAction("type");
            }
            onChange("");
            onBlur();
          }}
          options={getItems()}
          leftIcon={null}
        />
      </FieldGroup>
    </FieldWrapper>
  );
};

export const getReplaceContactNameFields =
  (refresh: () => void) =>
  (fieldProps: FieldProps & FieldComponentOverrideProps) => {
    const {
      field,
      index,
      groups,
      fields,
      visibility,
      submitResponses,
      responses,
    } = fieldProps;
    if (!field.field?.name || !searchableFieldNames.includes(field.field?.name))
      return undefined;

    const id = fieldName({ field, index });
    const errorId = fieldName({ field, index, suffix: "error" });
    const onSelect = async (item: Gql.ContactFieldsFragment) => {
      await saveContactInfo({
        index,
        contact: item,
        field,
        groups,
        fields,
        visibility,
        submitResponses,
        responses,
      });
      refresh();
    };

    return (
      <ContactSearchField
        {...fieldProps}
        contactType={
          field.field?.name?.startsWith("company/") ? "company" : "person"
        }
        id={id}
        errorId={errorId}
        onSelect={onSelect}
      />
    );
  };
